import React, { useCallback } from 'react';

import { PauseIcon, PlayArrowIcon } from '@change/ds-icons';

import { useI18n } from '@change-corgi/core/react/i18n';
import { ClientRender } from '@change-corgi/core/react/ssr/render';
import { useTracking } from '@change-corgi/core/react/tracking';

import type { TopicPageSupporterVoicesVideoComment } from 'src/app/pages/topic/pageContext';
import { VideoPlayer } from 'src/app/shared/components/petitionVideo';

import { useSupporterVoices } from '../../hooks/useSupporterVoices';
import { MessageLabel } from '../MessageLabel';

type VideoMessageProp = {
	supporterVoice: TopicPageSupporterVoicesVideoComment;
};

export function PlayPauseIcons({ isPlaying }: { isPlaying: boolean }): React.JSX.Element | null {
	return (
		<div className="pointer-events-none absolute bottom-4 right-4 z-[1] flex h-10 !w-10 items-center justify-center rounded-full bg-fill-interactive">
			{isPlaying ? <PauseIcon size={24} data-testid="pauseIcon" /> : <PlayArrowIcon size={32} data-testid="playIcon" />}
		</div>
	);
}

export const VideoMessage: React.FC<VideoMessageProp> = ({
	supporterVoice: { videoUrl, firstName: user, id, petitionSlug, petitionTitle, transcript },
}) => {
	const videoRef = React.useRef<HTMLVideoElement>(null);
	const [isPlaying, setIsPlaying] = React.useState(false);
	const track = useTracking();
	const {
		data: { topicName },
	} = useSupporterVoices();

	const { translate } = useI18n();

	const toggleVideoPlayback = useCallback(() => {
		if (videoRef.current) {
			if (videoRef.current.paused) {
				void videoRef.current.play();
				setIsPlaying(true);
			} else {
				videoRef.current.pause();
				setIsPlaying(false);
			}
		}

		void track(isPlaying ? 'video_playback_paused' : 'video_playback_started', {
			petition_id: `petition_${id}`,
			video_id: id,
			view_context: 'topic_page',
		});
	}, [videoRef, isPlaying, id, track]);

	return (
		<div data-testid="supporter-voices-video" data-qa="supporter-voices-video-message">
			<div className="relative flex h-[326px] max-w-[75vw] cursor-pointer [&>div]:w-full">
				<ClientRender>
					<PlayPauseIcons isPlaying={isPlaying} />
				</ClientRender>
				<VideoPlayer
					data-testid="video-message"
					className="h-full w-full object-cover"
					creator={user}
					ref={videoRef}
					tabIndex={0}
					videoUrl={videoUrl}
					videoId={id}
					playsInline
					onClick={() => {
						void toggleVideoPlayback();
					}}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							void toggleVideoPlayback();
						}
					}}
					onPause={(_) => {
						setIsPlaying(false);
					}}
				/>
				{transcript && (
					<div className="sr-only" aria-label={translate('corgi.topic_page.supporter_voices.video_transcript')}>
						{transcript}
					</div>
				)}
			</div>
			<MessageLabel
				name={user}
				petition={{
					href: `/p/${petitionSlug}?utm_source=topic_${topicName}_supporter_voices_video_comment_${id}`,
					title: petitionTitle,
				}}
			/>
		</div>
	);
};
